"use client";

import type React from "react";
import { Card, Progress, Typography, Space, Tag, Tooltip } from "antd";
import {
  DownloadOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useState, useEffect } from "react";
import { useDownloadMusic } from "../../context/DownloadAudioProvider";

const { Text, Title } = Typography;

export const DownloadProgressMonitor: React.FC = () => {
  const { downloadStates, currentlyDownloading } = useDownloadMusic();
  const [downloadSpeeds, setDownloadSpeeds] = useState<Record<string, number>>(
    {}
  );
  const [lastProgressUpdate, setLastProgressUpdate] = useState<
    Record<string, { time: number; progress: number }>
  >({});

  // Track download speeds
  useEffect(() => {
    const activeDownloads = Object.entries(currentlyDownloading).filter(
      ([id]) => downloadStates[id]?.isDownloading
    );

    if (activeDownloads.length === 0) return;

    // Update download speeds every second
    const interval = setInterval(() => {
      activeDownloads.forEach(([id]) => {
        const currentProgress = downloadStates[id]?.progress || 0;
        const currentTime = Date.now();

        if (lastProgressUpdate[id]) {
          const { time, progress } = lastProgressUpdate[id];
          const timeDiff = currentTime - time; // in ms
          const progressDiff = currentProgress - progress; // in percentage points

          if (timeDiff > 0 && progressDiff > 0) {
            // Calculate speed in percentage points per second
            const speed = (progressDiff / timeDiff) * 1000;

            setDownloadSpeeds((prev) => ({
              ...prev,
              [id]: speed,
            }));
          }
        }

        // Update last progress
        setLastProgressUpdate((prev) => ({
          ...prev,
          [id]: { time: currentTime, progress: currentProgress },
        }));
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [currentlyDownloading, downloadStates, lastProgressUpdate]);

  const activeDownloads = Object.entries(currentlyDownloading).filter(
    ([id]) => downloadStates[id]?.isDownloading
  );

  if (activeDownloads.length === 0) {
    return null;
  }

  // Calculate estimated time remaining
  const getEstimatedTimeRemaining = (id: string) => {
    const speed = downloadSpeeds[id] || 0;
    const currentProgress = downloadStates[id]?.progress || 0;
    const remaining = 100 - currentProgress;

    if (speed <= 0) return "Calculating...";

    // Time in seconds
    const timeRemaining = remaining / speed;

    if (timeRemaining < 60) {
      return `${Math.round(timeRemaining)}s`;
    } else if (timeRemaining < 3600) {
      return `${Math.round(timeRemaining / 60)}m ${Math.round(
        timeRemaining % 60
      )}s`;
    } else {
      return `${Math.floor(timeRemaining / 3600)}h ${Math.round(
        (timeRemaining % 3600) / 60
      )}m`;
    }
  };
  return (
    <Card
      className="mb-4 dark:bg-stone-800 dark:border-stone-700"
      title={
        <Space className="text-gray-900 dark:text-stone-100">
          <DownloadOutlined />
          <Title level={5} className="!mb-0 text-gray-900 dark:text-stone-100">
            <span className="text-gray-900 dark:text-stone-100">
              Active Downloads ({activeDownloads.length})
            </span>
          </Title>
        </Space>
      }
    >
      <Space direction="vertical" className="w-full" size="middle">
        {activeDownloads.map(([id, music]) => {
          const downloadState = downloadStates[id];
          return (
            <div key={id} className="w-full">
              <div className="flex justify-between items-center mb-2">
                <div className="flex-1 min-w-0">
                  <Text strong className="block truncate dark:text-stone-100">
                    <span className="text-gray-900 dark:text-stone-100">
                      {music.title}
                    </span>
                  </Text>
                  <Text
                    type="secondary"
                    className="text-sm dark:text-stone-400"
                  >
                    <span className="text-gray-900 dark:text-stone-100">
                      {music.artist}
                    </span>
                  </Text>
                </div>
                <Space>
                  <Tooltip
                    title={`Estimated time remaining: ${getEstimatedTimeRemaining(
                      id
                    )}`}
                  >
                    <InfoCircleOutlined className="dark:text-stone-400" />
                  </Tooltip>
                  <Tag color="processing" icon={<ClockCircleOutlined />}>
                    {downloadState?.progress || 0}%
                  </Tag>
                </Space>
              </div>
              <Progress
                percent={downloadState?.progress || 0}
                status="active"
                strokeColor={{
                  "0%": "#108ee9", // These colors might need dark mode adjustment if too bright/dark
                  "100%": "#87d068",
                }}
                format={(percent) => `${percent?.toFixed(1)}%`}
              />
              <div className="flex justify-between text-xs text-gray-500 dark:text-stone-500 mt-1">
                <span>Speed: {(downloadSpeeds[id] || 0).toFixed(2)}%/s</span>
                <span>ETA: {getEstimatedTimeRemaining(id)}</span>
              </div>
            </div>
          );
        })}
      </Space>
    </Card>
  );
};
